import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { X, Cookie, Settings, Check } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const COOKIE_CONSENT_KEY = 'udorm_cookie_consent';

export default function CookieConsentBanner() {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: true,
    functional: true,
    preferences: true,
  });

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      ...preferences,
      analytics: true,
      functional: true,
      preferences: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleAcceptSelected = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleRejectNonEssential = () => {
    const consent = {
      essential: true,
      analytics: false,
      functional: false,
      preferences: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-3 sm:p-4 animate-slide-up">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Main Banner */}
          {!showSettings ? (
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-[#C85A42]/10 rounded-lg flex-shrink-0">
                  <Cookie className="w-4 h-4 text-[#C85A42]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    We use cookies
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mb-3 leading-relaxed">
                    We use cookies to enhance your experience, analyze traffic, and improve our services. See our{' '}
                    <Link to="/cookie-policy" className="text-[#C85A42] hover:underline">Cookie Policy</Link>{' '}and{' '}
                    <Link to="/privacy-policy" className="text-[#C85A42] hover:underline">Privacy Policy</Link>.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      onClick={handleAcceptAll}
                      size="sm"
                      className="bg-[#C85A42] hover:bg-[#B04A35] text-white h-8 px-3 text-xs"
                      data-testid="cookie-accept-all"
                    >
                      <Check className="w-3.5 h-3.5 mr-1.5" />
                      Accept All
                    </Button>
                    <Button
                      onClick={handleRejectNonEssential}
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 text-xs"
                      data-testid="cookie-reject"
                    >
                      Essential Only
                    </Button>
                    <Button
                      onClick={() => setShowSettings(true)}
                      variant="ghost"
                      size="sm"
                      className="text-gray-600 dark:text-gray-400 h-8 px-2 text-xs"
                      data-testid="cookie-settings"
                    >
                      <Settings className="w-3.5 h-3.5 mr-1" />
                      Customize
                    </Button>
                  </div>
                </div>
                <button
                  onClick={handleRejectNonEssential}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-0.5 flex-shrink-0"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            /* Settings Panel */
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Cookie Preferences
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                {/* Essential Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">Essential Cookies</h4>
                      <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                        Always Active
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Required for the website to function. Cannot be disabled.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="w-5 h-5 rounded border-gray-300 text-[#C85A42] cursor-not-allowed opacity-50"
                  />
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">Analytics Cookies</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Help us understand how visitors use our website.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                    className="w-5 h-5 rounded border-gray-300 text-[#C85A42] focus:ring-[#C85A42] cursor-pointer"
                  />
                </div>

                {/* Functional Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">Functional Cookies</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Enable enhanced functionality and personalization.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={(e) => setPreferences(p => ({ ...p, functional: e.target.checked }))}
                    className="w-5 h-5 rounded border-gray-300 text-[#C85A42] focus:ring-[#C85A42] cursor-pointer"
                  />
                </div>

                {/* Preference Cookies */}
                <div className="flex items-start justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">Preference Cookies</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Remember your settings like language and theme.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.preferences}
                    onChange={(e) => setPreferences(p => ({ ...p, preferences: e.target.checked }))}
                    className="w-5 h-5 rounded border-gray-300 text-[#C85A42] focus:ring-[#C85A42] cursor-pointer"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  onClick={handleRejectNonEssential}
                  variant="outline"
                >
                  Reject All
                </Button>
                <Button
                  onClick={handleAcceptSelected}
                  className="bg-[#C85A42] hover:bg-[#B04A35] text-white"
                >
                  Save Preferences
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
